// The 1003 as the LOS shows it (Interview1003ViewV2 / ...V2More), reduced to
// data: the same sections, sub-sections, labels, order and option wording,
// read off the V2 application object, so the review page shows exactly what
// the loan officer would see on the loan's 1003 tab. Empty values are dropped
// by the renderer, per the PRD.
//
// Every value carries the apply pass's name for the field (`prov`) so the
// review can tell which document filled it (loan_field_provenance).

import * as com from "../Common.js";

export type Role = "borrower" | "coborrower" | "";

export interface FieldItem {
  key: string;
  label: string;
  value: string; // formatted for display; "" hides the field
  raw?: string; // the stored value, for the "edited since extraction" check
  prov?: string[]; // provenance paths that would have filled this field
  span?: 1 | 2 | 3;
}

export interface RowItem {
  key: string;
  title: string;
  fields: FieldItem[];
  prov?: string[]; // provenance paths that identify the row itself
}

export interface TableRow {
  key: string;
  cells: string[];
  prov?: string[];
}

export interface SubSection {
  key: string; // also the LOS anchor id (1a, 2c, ...)
  title: string;
  kind: "fields" | "rows" | "table" | "declarations";
  items?: FieldItem[];
  rows?: RowItem[];
  columns?: string[];
  tableRows?: TableRow[];
  totalRow?: string[];
  footer?: FieldItem;
  note?: string;
}

export interface SectionModel {
  key: string; // s1, s2, ...
  code: string; // S1, S2, ...
  title: string;
  shared: boolean; // loan-level, the same for every borrower
  subs: SubSection[];
}

// ---------------------------------------------------------------------------
// Option wording, as the LOS renders it
// ---------------------------------------------------------------------------

const label = (map: Record<string, string>, v: any): string => {
  const s = `${v ?? ""}`.trim();
  if (!s) return "";
  return map[s] || s;
};

export const OPT = {
  suffix: { "": "" },
  citizenship: { citizen: "U.S. Citizen", permanent: "Permanent Resident Alien", alien: "Non-Permanent Resident Alien" },
  credit: { alone: "I am applying for individual credit", withcoborrower: "I am applying for joint credit" },
  marital: { married: "Married", separated: "Separated", unmarried: "Unmarried" },
  yesno: { yes: "Yes", no: "No" },
  unmarriedRel: {
    civilunion: "Civil union",
    domesticpartnership: "Domestic partnership",
    reciprocalrelationship: "Registered reciprocal beneficiary relationship",
    other: "Other",
  },
  housing: { rentfree: "No primary housing expense", own: "Own", rent: "Rent" },
  purpose: { purchase: "Purchase", refinance: "Rate/Term refinance", cashoutrefinance: "Cash-out refinance" },
  occupancy: { principal: "Primary Residence", secondhome: "Second Home", investment: "Investment", fhasecond: "FHA Secondary Residence" },
  propertyType: {
    singlefamilydetached: "Single family",
    singlefamilyattached: "Single family (attached)",
    puddetached: "PUD",
    pudattached: "PUD (attached)",
    twotofourfamily: "Multifamily (1-4 unit)",
    commercial: "Multifamily (5+ unit)",
    condominium: "Condo",
    mobilehome: "Manufactured home",
  },
  reoStatus: { sold: "Sold", pending: "Pending Sale", retained: "Retained" },
  liabilityCategory: { Installment: "Installment", Mortgage: "Lease", Open: "Open 30-Day", Revolving: "Revolving", Unknown: "Other" },
  alimonyType: {
    alimony: "Alimony",
    childsupport: "Child Support",
    maintenancepayments: "Separate Maintenance",
    jobrelatedexpenses: "Job Related Expenses",
    other: "Other",
  },
  giftType: { GiftOfCash: "Cash Gift", GiftOfPropertyEquity: "Gift of Equity", Grant: "Grant" },
  giftSource: {
    CommunityNonprofit: "Community Nonprofit",
    Employer: "Employer",
    FederalAgency: "Federal Agency",
    LocalAgency: "Local Agency",
    StateAgency: "State Agency",
    Lender: "Lender",
    Relative: "Relative",
    ReligiousNonprofit: "Religious Nonprofit",
    UnmarriedPartner: "Unmarried Partner",
    Other: "Other",
  },
  lien: { firstlien: "First lien", subordinatelien: "Subordinate lien" },
  ownership: { individual: "Individual", joint: "Joint", Individual: "Individual", Joint: "Joint" },
  propertyOwned: { principal: "Primary residence (PR)", fhasecond: "FHA secondary residence (SR)", secondhome: "Second home (SH)", investment: "Investment property (IP)" },
  titleHeld: { yourself: "By yourself (S)", jointwithspouse: "Jointly with your spouse (SP)", jointwithother: "Jointly with another person (O)" },
};

const listLabel = (list: any[], v: any): string => {
  const s = `${v ?? ""}`.trim();
  if (!s) return "";
  const hit = (list || []).find((o: any) => o[0] === s);
  return hit ? hit[1] : s;
};

// ---------------------------------------------------------------------------
// Formatting, as the LOS's read-only previews do it
// ---------------------------------------------------------------------------

const clean = (v: any): string => `${v ?? ""}`.trim();

export const money = (v: any): string => {
  const s = clean(v).replace(/[$,\s]/g, "");
  if (s === "") return "";
  const n = Number(s);
  if (!isFinite(n)) return clean(v);
  return "$ " + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const moneyNumber = (v: any): number => {
  const s = clean(v).replace(/[$,\s]/g, "");
  const n = Number(s);
  return isFinite(n) && s !== "" ? n : 0;
};

// The LOS shows an income stored as yearly divided by twelve.
const monthlyOf = (amount: any, period: any): string => {
  const n = moneyNumber(amount);
  if (!n) return "";
  return money(`${period}`.toLowerCase() === "yearly" ? n / 12 : n);
};

const maskSSN = (v: any): string => {
  const digits = clean(v).replace(/\D/g, "");
  if (!digits) return "";
  return `•••-••-${digits.slice(-4)}`;
};

const phone = (v: any): string => {
  const s = clean(v);
  if (!s) return "";
  try {
    return com.fixPhoneInput(s) || s;
  } catch (e) {
    return s;
  }
};

const addressLine = (a: any): string => {
  if (!a) return "";
  const street = [clean(a.street), clean(a.unit)].filter(Boolean).join(", ");
  const cityState = [clean(a.city), [clean(a.state), clean(a.zip)].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [street, cityState].filter(Boolean).join(", ");
};

const duration = (years: any, months: any): string => {
  const y = clean(years);
  const m = clean(months);
  if (!y && !m) return "";
  const parts = [];
  if (y) parts.push(`${y} year${y === "1" ? "" : "s"}`);
  if (m) parts.push(`${m} month${m === "1" ? "" : "s"}`);
  return parts.join(" ");
};

const employerAddress = (o: any): string => {
  if (!o) return "";
  const structured = addressLine({ street: o.employerstreet, unit: o.employerunit, city: o.employercity, state: o.employerstate, zip: o.employerzip });
  return structured || clean(o.employeraddress);
};

const f = (key: string, label: string, value: string, extra: Partial<FieldItem> = {}): FieldItem => ({
  key,
  label,
  value,
  raw: extra.raw ?? value,
  ...extra,
});

const nonEmpty = (items: FieldItem[]) => items.filter((i) => clean(i.value) !== "");

// ---------------------------------------------------------------------------
// Parties
// ---------------------------------------------------------------------------

export interface PartyInfo {
  partyId: string;
  role: Role;
  name: string;
  isPrimary: boolean;
}

export const listParties = (app: any): PartyInfo[] => {
  if (!app) return [];
  const byId = app.participantsById || {};
  const order: string[] = app.participantOrder?.length ? app.participantOrder : Object.keys(byId);
  const primary = app.primaryPartyId || order[0];
  let coSeen = false;
  // Same test as Interview1003ViewV2's party switcher: a real co-borrower
  // comes back with isBorrower=false and role="coborrower", so filtering on
  // isBorrower alone dropped every co-borrower from the review.
  const isBorrowingParty = (p: any) =>
    !!p && (p.isBorrower || p.isPrimary || p.role === "borrower" || p.role === "coborrower");
  return order
    .filter((id) => isBorrowingParty(byId[id]))
    .map((id) => {
      const p = byId[id];
      const isPrimary = id === primary || !!p.isPrimary;
      let role: Role = "";
      if (isPrimary) role = "borrower";
      else if (!coSeen) {
        role = "coborrower";
        coSeen = true;
      }
      return {
        partyId: id,
        role,
        isPrimary,
        name: [p.firstName, p.lastName].filter(Boolean).join(" ") || (isPrimary ? "Borrower" : "Co-borrower"),
      };
    });
};

// ---------------------------------------------------------------------------
// Section 1
// ---------------------------------------------------------------------------

const section1 = (app: any, P: any): SubSection[] => {
  const items1a: FieldItem[] = [
    f("firstName", "First name", clean(P.firstName), { prov: ["firstname"] }),
    f("middleName", "Middle name", clean(P.middleName)),
    f("lastName", "Last name", clean(P.lastName), { prov: ["lastname"] }),
    f("suffix", "Suffix", clean(P.suffix)),
    f("ssn", "Social security number", maskSSN(P.socialSecurity), { raw: clean(P.socialSecurity), prov: ["socialsecurity"] }),
    f("dob", "Date of birth", clean(P.dateOfBirth), { prov: ["dateofbirth"] }),
    f("citizenship", "Citizenship", label(OPT.citizenship, P.citizenship)),
    f("visa", "Visa expiration", clean(P.visaExpiration), { prov: ["visaexpiration"] }),
    f("credit", "Type of credit", label(OPT.credit, app.hascoborrower), { span: 2 }),
    f("marital", "Marital status", label(OPT.marital, P.maritalStatus)),
    f("unmarriedRel", "Unmarried relationship", label(OPT.unmarriedRel, P.unmarriedRelationship)),
    f("dependents", "Dependents (not listed by another borrower)", clean(P.numberOfDependents)),
    f("dependentAges", "Age of dependents", clean(P.dependentsAges)),
    f("email", "Email", clean(P.email)),
    f("homePhone", "Home phone", phone(P.homePhone)),
    f("mobilePhone", "Mobile phone", phone(P.mobilePhone)),
    f("workPhone", "Work phone", phone(P.workPhone)),
    f("presentAddress", "Current residential address", addressLine(P.presentAddress), {
      span: 2,
      raw: clean(P.presentAddress?.street),
      prov: ["presentaddressstreet", "presentaddresscity", "presentaddressstate", "presentaddresszip", "presentaddressunit"],
    }),
    f("presentDuration", "How long have you been at your current address?", duration(P.presentAddress?.years, P.presentAddress?.months)),
    f("housing", "Housing", label(OPT.housing, P.residenceOwnership), { raw: clean(P.residenceOwnership), prov: ["presentaddressownership"] }),
    f("rent", "Monthly Rent", P.residenceOwnership === "rent" ? money(P.monthlyRent) : "", { raw: clean(P.monthlyRent), prov: ["expenses.currentrent"] }),
  ];
  (P.previousAddresses || []).forEach((a: any, i: number) => {
    items1a.push(f(`prev${i}`, "Previous residential address", addressLine(a), { span: 2 }));
    items1a.push(f(`prevDur${i}`, "How long have you been at your previous address?", duration(a.years, a.months)));
    items1a.push(f(`prevHousing${i}`, "Housing", label(OPT.housing, a.ownership)));
    items1a.push(f(`prevRent${i}`, "Monthly Rent", a.ownership === "rent" ? money(a.rent) : ""));
  });
  if (P.sameMailingAddress === false) {
    items1a.push(f("mailing", "Mailing Address", addressLine(P.mailingAddress), { span: 2 }));
  }

  const O = P.occupation || {};
  const inc = O.income || {};
  const items1b: FieldItem[] = [
    f("employer", "Employer or business name", clean(O.employername), { prov: ["occupation.employername"] }),
    f("empPhone", "Phone", phone(O.phone)),
    f("empAddress", "Address", employerAddress(O), { span: 2, raw: clean(O.employerstreet || O.employeraddress), prov: ["occupation.employeraddress"] }),
    f("position", "Position or title", clean(O.positiontitle), { prov: ["occupation.positiontitle"] }),
    f("start", "Start date", clean(O.from)),
    f("lineOfWork", "How long in this line of work?", duration(O.yearsatlineofwork, O.monthsatlineofwork)),
    f("byAgent", "Employed by a party to the transaction", O.employedbyagent ? "Yes" : ""),
    f("selfEmployed", "Business owner or self-employed", O.selfemployed ? "Yes" : "", { raw: O.selfemployed ? "true" : "", prov: ["occupation.selfemployed"] }),
  ];
  let total = 0;
  if (O.selfemployed) {
    const share = clean(O.percentownership);
    items1b.push(
      f("ownershipShare", "Ownership share", share ? (Number(share) < 25 ? "less than 25%" : "25% or more") : "", { raw: share, prov: ["occupation.percentownership"] })
    );
    const monthly = monthlyOf(O.incomeorloss, O.incomeorlossperiod);
    items1b.push(f("incomeOrLoss", "Monthly Income (or Loss)", monthly, { raw: clean(O.incomeorloss), prov: ["occupation.incomeorloss"] }));
    total += moneyNumber(monthly.replace("$", ""));
  } else {
    // Not a tuple type: CRA 3.4 lints TS through typescript-estree 2.x, which
    // still reads TupleType.elementTypes (renamed in TypeScript 4.0) and crashes
    // the production build with "Cannot read properties of undefined (reading 'map')".
    const parts: string[][] = [
      ["base", "Base", "occupation.income.base"],
      ["overtime", "Overtime", "occupation.income.overtime"],
      ["bonuses", "Bonus", "occupation.income.bonuses"],
      ["commissions", "Commission", "occupation.income.commissions"],
      ["military", "Military", "occupation.income.military"],
      ["other", "Other", "occupation.income.other"],
    ];
    parts.forEach(([k, l, prov]) => {
      const monthly = monthlyOf(inc[k], inc[`${k}monthly`]);
      if (monthly) total += moneyNumber(monthly.replace("$", ""));
      items1b.push(f(`inc_${k}`, `${l} (monthly)`, monthly, { raw: clean(inc[k]), prov: [prov] }));
    });
  }

  const rows1c: RowItem[] = (P.otheroccupations || []).map((o: any, i: number) => {
    const oi = o.income || {};
    const fields: FieldItem[] = [
      f("employer", "Employer or business name", clean(o.employername)),
      f("phone", "Phone", phone(o.phone)),
      f("address", "Address", employerAddress(o), { span: 2 }),
      f("position", "Position or title", clean(o.positiontitle)),
      f("start", "Start date", clean(o.from)),
      f("lineOfWork", "How long have you been in this line of work?", duration(o.yearsatlineofwork, o.monthsatlineofwork)),
      f("selfEmployed", "Business owner or self-employed", o.selfemployed ? "Yes" : ""),
    ];
    if (o.selfemployed) {
      fields.push(f("incomeOrLoss", "Monthly Income (or Loss)", monthlyOf(o.incomeorloss, o.incomeorlossperiod)));
    } else {
      ["base", "overtime", "bonuses", "commissions", "military", "other"].forEach((k) => {
        fields.push(f(`inc_${k}`, `${k === "bonuses" ? "Bonus" : k === "commissions" ? "Commission" : k[0].toUpperCase() + k.slice(1)} (monthly)`, monthlyOf(oi[k], oi[`${k}monthly`])));
      });
    }
    return {
      key: o.key || `other-${i}`,
      title: clean(o.employername) || `Additional employment ${i + 1}`,
      fields: nonEmpty(fields),
      prov: o.key ? [`otheroccupations[${o.key}]`] : [],
    };
  });

  const rows1d: RowItem[] = (P.previousoccupations || []).map((o: any, i: number) => ({
    key: o.key || `prev-${i}`,
    title: clean(o.employername) || `Previous employment ${i + 1}`,
    fields: nonEmpty([
      f("employer", "Employer or business name", clean(o.employername)),
      f("position", "Position or title", clean(o.positiontitle)),
      f("address", "Address", employerAddress(o), { span: 2 }),
      f("from", "From", clean(o.from)),
      f("to", "To", clean(o.to)),
      f("selfEmployed", "Business Owner or Self-Employed", o.selfemployed ? "Yes" : ""),
      f("income", "Income (monthly)", monthlyOf(o.income, o.incomeperiod)),
    ]),
  }));

  const other = (P.income && P.income.otherincome) || [];
  const table1e: TableRow[] = other.map((o: any, i: number) => ({
    key: o.key || `oi-${i}`,
    cells: [listLabel(com.othersouresofincome, o.incometype) || clean(o.description), money(o.amount) ? `${money(o.amount)} / month` : ""],
    prov: o.key ? [`income.otherincome[${o.key}]`] : [],
  }));

  return [
    { key: "1a", title: "1a. Personal Information", kind: "fields", items: nonEmpty(items1a) },
    {
      key: "1b",
      title: "1b. Current Employment/Self-Employment and Income",
      kind: "fields",
      items: nonEmpty(items1b),
      footer: total ? f("total", "Total income", `${money(total)} / month`) : undefined,
    },
    { key: "1c", title: "1c. Additional Employment/Self-Employment and Income", kind: "rows", rows: rows1c },
    { key: "1d", title: "1d. Previous Employment/Self-Employment and Income", kind: "rows", rows: rows1d },
    { key: "1e", title: "1e. Income from Other Sources", kind: "table", columns: ["Income source", "Monthly income"], tableRows: table1e.filter((r) => r.cells.some(Boolean)) },
  ];
};

// ---------------------------------------------------------------------------
// Section 2
// ---------------------------------------------------------------------------

const ownershipCell = (row: any, showOwnership: boolean): string => {
  if (!showOwnership) return "";
  return label(OPT.ownership, row.ownership) || (row.ownerPartyIds?.length > 1 ? "Joint" : "Individual");
};

const section2 = (app: any, pid: string): SubSection[] => {
  const showOwnership = listParties(app).length > 1;
  const A = (app.finance && app.finance.assetsByPartyId && app.finance.assetsByPartyId[pid]) || {};
  const L = (app.finance && app.finance.liabilitiesByPartyId && app.finance.liabilitiesByPartyId[pid]) || {};

  const accounts: TableRow[] = (A.accounts || []).map((a: any, i: number) => ({
    key: a.key || `acct-${i}`,
    cells: [
      listLabel(com.bankaccounttypes, a.accountType),
      clean(a.bankName),
      clean(a.account),
      money(a.balance),
      ownershipCell(a, showOwnership),
    ],
    prov: [`assetsandliabilities.accounts[${clean(a.account)}]`],
  }));
  const accountTotal = (A.accounts || []).reduce((s: number, a: any) => s + moneyNumber(a.balance), 0);

  const otherAssets: TableRow[] = (A.otherAssets || []).map((a: any, i: number) => ({
    key: a.key || `oa-${i}`,
    cells: [listLabel(com.otherassetsandcredits, a.assetType), money(a.value), ownershipCell(a, showOwnership)],
    prov: a.key ? [`assetsandliabilities.otherassets[${a.key}]`] : [],
  }));

  const loans: TableRow[] = (L.loans || []).map((l: any, i: number) => ({
    key: l.key || `loan-${i}`,
    cells: [
      label(OPT.liabilityCategory, l.creditLoanType || l.creditLiabilityAccountType),
      clean(l.name),
      clean(l.accountNumber),
      money(l.remainingBalance),
      money(l.monthlyPayment) ? `${money(l.monthlyPayment)} / mo` : "",
      label(OPT.yesno, l.paidOffAtClosing),
      ownershipCell(l, showOwnership),
    ],
    prov: l.key ? [`assetsandliabilities.liabilities[${l.key}]`] : [],
  }));

  const expenses = (L.additionalExpenses && L.additionalExpenses.length ? L.additionalExpenses : (app.participantsById?.[pid]?.income?.alimony || [])) as any[];
  const expenseRows: TableRow[] = expenses.map((e: any, i: number) => ({
    key: e.key || `exp-${i}`,
    cells: [label(OPT.alimonyType, e.alimonyorsupport), money(e.amount)],
    prov: e.key ? [`income.alimony[${e.key}]`] : [],
  }));

  const ownershipCol = showOwnership ? ["Ownership"] : [];
  return [
    {
      key: "2a",
      title: "2a. Assets – Bank Accounts, Retirement, and Other Accounts You Have",
      kind: "table",
      columns: ["Account type", "Financial institution", "Account number", "Cash/market value", ...ownershipCol],
      tableRows: accounts.map((r) => ({ ...r, cells: showOwnership ? r.cells : r.cells.slice(0, 4) })),
      totalRow: accounts.length > 1 ? ["Total", "", "", money(accountTotal), ...(showOwnership ? [""] : [])] : undefined,
    },
    {
      key: "2b",
      title: "2b. Other Assets and Credits You Have",
      kind: "table",
      columns: ["Asset or credit type", "Cash/market value", ...ownershipCol],
      tableRows: otherAssets.map((r) => ({ ...r, cells: showOwnership ? r.cells : r.cells.slice(0, 2) })),
    },
    {
      key: "2c",
      title: "2c. Liabilities – Credit Cards, Other Debts, and Leases that You Owe",
      kind: "table",
      columns: ["Category", "Company Name", "Account Number", "Unpaid Balance", "Mo. Payment", "Paid off at/before closing", ...ownershipCol],
      tableRows: loans.map((r) => ({ ...r, cells: showOwnership ? r.cells : r.cells.slice(0, 6) })),
    },
    {
      key: "2d",
      title: "2d. Other Liabilities and Expenses",
      kind: "table",
      columns: ["Type", "Monthly payment"],
      tableRows: expenseRows,
    },
  ];
};

// ---------------------------------------------------------------------------
// Section 3
// ---------------------------------------------------------------------------

const normStreet = (s: string) => clean(s).toLowerCase().replace(/[^a-z0-9]/g, "");

const section3 = (app: any, pid: string): SubSection[] => {
  const R = (app.finance && app.finance.realEstateByPartyId && app.finance.realEstateByPartyId[pid]) || {};
  const rows: RowItem[] = (R.properties || []).map((p: any, i: number) => {
    const key = clean(p.key);
    const prov: string[] = [];
    if (key.startsWith("parsed:lease:") || p.street || p.address) {
      prov.push(`ownedproperties[street:${normStreet(p.street || p.address)}]`);
    }
    const m = key.match(/^parsed:(\d+)$/);
    const pos = m ? m[1] : `${i}`;
    const fieldProv = (field: string) => [`ownedproperties.${pos}.${field}`];
    const address = addressLine({ street: p.street || p.address, unit: p.unit, city: p.city, state: p.state, zip: p.zip });
    const fields: FieldItem[] = [
      f("address", "Address", address, { span: 2, raw: clean(p.street || p.address), prov: fieldProv("address") }),
      f("value", "Property value", money(p.marketValue), { raw: clean(p.marketValue), prov: fieldProv("marketvalue") }),
      f("status", "Status", label(OPT.reoStatus, p.status), { raw: clean(p.status), prov: fieldProv("status") }),
      f("occupancy", "Intended occupancy", label(OPT.occupancy, p.occupancy), { raw: clean(p.occupancy), prov: fieldProv("occupancy") }),
      f("type", "Property type", label(OPT.propertyType, p.propertyType), { raw: clean(p.propertyType), prov: fieldProv("propertytype") }),
      f("expenses", "Monthly insurance, taxes, association dues, etc.", money(p.insuranceMaintenanceTaxes), {
        raw: clean(p.insuranceMaintenanceTaxes),
        prov: fieldProv("insurancemaintenancetaxes"),
      }),
      f("gross", "Monthly rental income", money(p.grossRentalIncome), { raw: clean(p.grossRentalIncome), prov: fieldProv("grossrentalincome") }),
      f("net", "Net monthly rental income", money(p.netRentalIncome), { raw: clean(p.netRentalIncome), prov: fieldProv("netrentalincome") }),
    ];
    (p.loans || []).forEach((l: any, j: number) => {
      fields.push(f(`loan${j}creditor`, "Creditor", clean(l.name)));
      fields.push(f(`loan${j}balance`, "Unpaid balance", money(l.remainingBalance)));
      fields.push(f(`loan${j}payment`, "Monthly mortgage payment", money(l.monthlyPayment)));
    });
    return {
      key: key || `reo-${i}`,
      title: address || `Property ${i + 1}`,
      fields: nonEmpty(fields),
      prov,
    };
  });
  return [{ key: "3a", title: "3a. Property You Own", kind: "rows", rows }];
};

// ---------------------------------------------------------------------------
// Section 4
// ---------------------------------------------------------------------------

const section4 = (app: any): SubSection[] => {
  const P = app.property || {};
  const S = app.selection || {};
  const purpose = clean(P.purpose);
  const isPurchase = purpose === "purchase";
  const value = isPurchase ? P.salesprice : P.appraisal;
  const items4a: FieldItem[] = [
    f("purpose", "Loan purpose", label(OPT.purpose, purpose)),
    f("occupancy", "Intended occupancy", label(OPT.occupancy, P.occupancy)),
    f("value", "Property value", money(value), { raw: clean(value), prov: isPurchase ? ["property.salesprice"] : ["property.appraisal"] }),
    f("balance", "Current balance", !isPurchase ? money(app.loanbalance) : ""),
    f("amount", "Loan amount", money(S.loansize), { raw: clean(S.loansize), prov: ["selection.loansize"] }),
    f("type", "Property type", label(OPT.propertyType, P.propertytype)),
    f("units", "Number of units", clean(P.numberofunits)),
    f("someoneElse", "Buying the property for someone else", P.buyforsomeoneelse ? "Yes" : ""),
    f("address", "Address", addressLine({ street: P.address, unit: P.unit, city: P.city, state: P.state, zip: P.zipcode }), {
      span: 3,
      raw: clean(P.address),
      prov: ["property.address", "property.city", "property.state", "property.zipcode", "property.county"],
    }),
    f("insurance", "Insurance (monthly)", money(P.insurancemain), { raw: clean(P.insurancemain), prov: ["property.expenses.homeownersinsurance"] }),
    f("tax", "Tax (monthly)", money(P.insurancetax), { raw: clean(P.insurancetax), prov: ["property.expenses.realestatetaxes"] }),
    f("hoa", "HOA fee (monthly)", money(P.insurancehoa), { raw: clean(P.insurancehoa), prov: ["property.expenses.hoa"] }),
    f("pmi", "PMI (monthly)", money(P.insurancepmi)),
    f("mixed", "Mixed-use property", P.isMixedUse ? "Yes" : ""),
    f("manufactured", "Manufactured home", P.isManufacturedHome ? "Yes" : ""),
  ];
  if (P.isManufacturedHome && P.mobilehomeinfo) {
    const M = P.mobilehomeinfo;
    items4a.push(
      f("mhPark", "Manufactured home park full name", clean(M.mobilehomeparkfullname)),
      f("mhParkAddress", "Park Address", addressLine({ street: M.parkStreet || M.parkAddress, unit: M.parkUnit, city: M.parkCity, state: M.parkState, zip: M.parkZip }), { span: 2 }),
      f("mhRent", "Monthly space rent", money(M.leasedSpaceRentAmount)),
      f("mhSpace", "Space number", clean(M.space)),
      f("mhMake", "Manufacturer make", clean(M.manufacturerMake)),
      f("mhModel", "Manufacturer model", clean(M.manufacturerModel)),
      f("mhYear", "Year built", clean(M.year)),
      f("mhType", "Type", clean(M.mobilehometype)),
      f("mhCondition", "Condition", clean(M.mobilehomedescription))
    );
  }

  const primary = app.primaryPartyId;
  const NM = (app.finance?.liabilitiesByPartyId?.[primary]?.newMortgages || []) as any[];
  const rows4b: RowItem[] = NM.map((m: any, i: number) => ({
    key: m.key || `nm-${i}`,
    title: clean(m.creditor) || `Mortgage loan ${i + 1}`,
    fields: nonEmpty([
      f("creditor", "Creditor name", clean(m.creditor)),
      f("lien", "Lien type", label(OPT.lien, m.lienType)),
      f("monthly", "Monthly amount", money(m.monthlyAmount) ? `${money(m.monthlyAmount)} / mo` : ""),
      f("amount", "Loan amount", money(m.loanAmount)),
      f("limit", "Credit limit", money(m.creditLimit)),
    ]),
  }));

  const items4c: FieldItem[] = [];
  const rental = isPurchase && (P.occupancy === "investment" || (P.propertytype === "twotofourfamily" && P.occupancy === "principal"));
  if (rental) {
    items4c.push(
      f("expectedGross", "Expected monthly rental income", money(P.purchase?.expectedgrossmonthlyrentalincome)),
      f("expectedNet", "Expected net monthly income", money(P.purchase?.expectednetmonthlyrentalincome))
    );
  }

  const parties = listParties(app);
  const rows4d: RowItem[] = [];
  parties.forEach((party) => {
    const gifts = (app.finance?.assetsByPartyId?.[party.partyId]?.gifts || []) as any[];
    gifts.forEach((g: any, i: number) => {
      rows4d.push({
        key: g.key || `${party.partyId}-gift-${i}`,
        title: `${label(OPT.giftType, g.assetType) || "Gift"}${parties.length > 1 ? ` · ${party.name}` : ""}`,
        fields: nonEmpty([
          f("type", "Asset or credit type", label(OPT.giftType, g.assetType)),
          f("deposited", "Deposited", g.deposited ? "Deposited" : "Not Deposited"),
          f("source", "Source", label(OPT.giftSource, g.source) + (g.sourceDescription ? ` (${clean(g.sourceDescription)})` : "")),
          f("value", "Cash/market value", money(g.value)),
        ]),
        prov: g.key ? [`assetsandliabilities.gifts[${g.key}]`] : [],
      });
    });
  });

  return [
    { key: "4a", title: "4a. Loan and Property Information", kind: "fields", items: nonEmpty(items4a) },
    { key: "4b", title: "4b. Other New Mortgage Loans on the Property You are Buying or Refinancing", kind: "rows", rows: rows4b },
    { key: "4c", title: "4c. Rental Income on the Property You Want to Purchase (For Purchase Only)", kind: "fields", items: nonEmpty(items4c) },
    { key: "4d", title: "4d. Gifts or Grants You Have Been Given or Will Receive for this Loan", kind: "rows", rows: rows4d },
  ];
};

// ---------------------------------------------------------------------------
// Section 5
// ---------------------------------------------------------------------------

export interface DeclarationLine {
  id: string;
  label: string;
  answer: string;
  key: string;
}

export const DECLARATIONS: { id: string; key: string; label: string; section: "5a" | "5b" }[] = [
  { id: "A", key: "occupyasprimary", label: "A. Will you occupy the property as your primary residence?", section: "5a" },
  { id: "B", key: "familyrelationship", label: "B. If this is a Purchase Transaction: Do you have a family relationship or business affiliation with the seller of the property?", section: "5a" },
  { id: "C", key: "downpaymentborrowed", label: "C. Are you borrowing any money for this real estate transaction or obtaining any money from another party, such as the seller or realtor, that you have not disclosed on this loan application?", section: "5a" },
  { id: "D1", key: "applyingonanother", label: "D. 1. Have you or will you be applying for a mortgage loan on another property (not the property securing this loan) on or before closing this transaction that is not disclosed on this loan application?", section: "5a" },
  { id: "D2", key: "applyingnewcredit", label: "D. 2. Have you or will you be applying for any new credit on or before closing this loan that is not disclosed on this application?", section: "5a" },
  { id: "E", key: "cleanenergylien", label: "E. Will this property be subject to a lien that could take priority over the first mortgage lien, such as a clean energy lien paid through your property taxes (e.g., the Property Assessed Clean Energy Program)?", section: "5a" },
  { id: "F", key: "comakeronnote", label: "F. Are you a co-signer or guarantor on any debt or loan that is not disclosed on this application?", section: "5b" },
  { id: "G", key: "judgements", label: "G. Are there any outstanding judgments against you?", section: "5b" },
  { id: "H", key: "delinquentondebt", label: "H. Are you currently delinquent or in default on a Federal debt?", section: "5b" },
  { id: "I", key: "lawsuit", label: "I. Are you a party to a lawsuit in which you potentially have any personal financial liability?", section: "5b" },
  { id: "J", key: "conveyedtitle", label: "J. Have you conveyed title to any property in lieu of foreclosure in the past 7 years?", section: "5b" },
  { id: "K", key: "loanforeclosure", label: "K. Within the past 7 years, have you completed a pre-foreclosure sale or short sale, whereby the property was sold to a third party and the Lender agreed to accept less than the outstanding mortgage balance due?", section: "5b" },
  { id: "L", key: "propertyforeclosure", label: "L. Have you had property foreclosed upon in the last 7 years?", section: "5b" },
  { id: "M", key: "bancrupt", label: "M. Have you declared bankruptcy within the past 7 years?", section: "5b" },
];

// The LOS shows an unanswered declaration as "No" (YesNo primitive).
const yesNoDisplay = (v: any) => (clean(v) === "yes" ? "Yes" : "No");

const section5 = (app: any, pid: string): SubSection[] => {
  const D = (app.declarationsByPartyId && app.declarationsByPartyId[pid]) || {};
  const lines = (section: "5a" | "5b"): FieldItem[] => {
    const out: FieldItem[] = [];
    DECLARATIONS.filter((d) => d.section === section).forEach((d) => {
      out.push(f(d.id, d.label, yesNoDisplay(D[d.key]), { raw: clean(D[d.key]) }));
      if (d.id === "A" && clean(D.occupyasprimary) === "yes") {
        out.push(f("A-own", "Have you had an ownership interest in another property in the last three years?", yesNoDisplay(D.ownershipinterest)));
        if (clean(D.ownershipinterest) === "yes") {
          out.push(f("A-1", "(1) What type of property did you own?", label(OPT.propertyOwned, D.propertyowned || "principal")));
          out.push(f("A-2", "(2) How did you hold title to the property?", label(OPT.titleHeld, D.titleheld || "yourself")));
        }
      }
      if (d.id === "C" && clean(D.downpaymentborrowed) === "yes") {
        out.push(f("C-amt", "What is the amount of this money?", money(D.moneyborrowed)));
      }
      if (d.id === "M" && clean(D.bancrupt) === "yes") {
        const chapters = ["7", "11", "12", "13"].filter((c) => D[`chapter${c}`]).map((c) => `Chapter ${c}`);
        out.push(f("M-ch", "Identify the type(s) of bankruptcy", chapters.join(", ")));
      }
    });
    return out;
  };
  return [
    { key: "5a", title: "5a. About this Property and Your Money for this Loan", kind: "declarations", items: lines("5a") },
    { key: "5b", title: "5b. About Your Finances", kind: "declarations", items: lines("5b") },
  ];
};

// ---------------------------------------------------------------------------
// Section 9
// ---------------------------------------------------------------------------

const section9 = (app: any): SubSection[] => {
  const B = app.brokerInfo || {};
  const items: FieldItem[] = [
    f("orgName", "Loan originator organization name", clean(B.name)),
    f("orgNmls", "Loan originator organization NMLSR ID", clean(B.nmls)),
    f("orgLicense", "State license ID", clean(B.licenseid)),
    f("orgAddress", "Address", addressLine({ street: B.street, unit: B.unit, city: B.city, state: B.state, zip: B.zip }), { span: 3 }),
    f("loName", "Loan originator name", clean(B.loanofficername)),
    f("loNmls", "Loan originator NMLSR ID", clean(B.loanofficernmls)),
    f("loLicense", "Loan originator state license ID", clean(B.loanofficerlicenseid)),
    f("email", "Email", clean(B.email)),
    f("phone", "Phone", phone(B.phone)),
  ];
  return [{ key: "9a", title: "Loan Originator Information", kind: "fields", items: nonEmpty(items) }];
};

// ---------------------------------------------------------------------------
// The whole review for one borrower
// ---------------------------------------------------------------------------

export const buildReviewModel = (app: any, partyId: string): SectionModel[] => {
  if (!app) return [];
  const P = (app.participantsById && app.participantsById[partyId]) || {};
  return [
    { key: "s1", code: "S1", title: "Borrower Information", shared: false, subs: section1(app, P) },
    { key: "s2", code: "S2", title: "Financial Information — Assets and Liabilities", shared: false, subs: section2(app, partyId) },
    { key: "s3", code: "S3", title: "Financial Information — Real Estate", shared: false, subs: section3(app, partyId) },
    { key: "s4", code: "S4", title: "Loan and Property Information", shared: true, subs: section4(app) },
    { key: "s5", code: "S5", title: "Declarations", shared: false, subs: section5(app, partyId) },
    { key: "s9", code: "S9", title: "Loan Originator Information", shared: true, subs: section9(app) },
  ];
};

// Short nav titles, as the LOS anchor uses them.
export const SECTION_NAV: Record<string, string> = {
  s1: "S1. Borrower Information",
  s2: "S2. Financial Information",
  s3: "S3. Real Estate",
  s4: "S4. Loan and Property Information",
  s5: "S5. Declarations",
  s9: "S9. Loan Originator Information",
};

// The monthly income the review header sums per borrower, the way 1b totals it.
export const monthlyIncomeOf = (app: any, partyId: string): number => {
  const P = (app?.participantsById && app.participantsById[partyId]) || {};
  const O = P.occupation || {};
  let total = 0;
  if (O.selfemployed) {
    total += moneyNumber(monthlyOf(O.incomeorloss, O.incomeorlossperiod).replace("$", ""));
  } else {
    const inc = O.income || {};
    ["base", "overtime", "bonuses", "commissions", "military", "other"].forEach((k) => {
      total += moneyNumber(monthlyOf(inc[k], inc[`${k}monthly`]).replace("$", ""));
    });
  }
  (P.otheroccupations || []).forEach((o: any) => {
    if (o.selfemployed) total += moneyNumber(monthlyOf(o.incomeorloss, o.incomeorlossperiod).replace("$", ""));
    else {
      const oi = o.income || {};
      ["base", "overtime", "bonuses", "commissions", "military", "other"].forEach((k) => {
        total += moneyNumber(monthlyOf(oi[k], oi[`${k}monthly`]).replace("$", ""));
      });
    }
  });
  ((P.income && P.income.otherincome) || []).forEach((o: any) => {
    total += moneyNumber(o.amount);
  });
  return total;
};

export const formatMoney = money;

// Comparable form of a value, for "was this changed since the document filled it".
export const comparable = (v: any): string => {
  const s = clean(v).toLowerCase();
  const n = s.replace(/[$,\s]/g, "");
  if (n !== "" && isFinite(Number(n))) return Number(n).toFixed(2);
  return s.replace(/[^a-z0-9]/g, "");
};
